"use client"

import { useEffect, useState } from "react"
import { getOpenState, type ShopHour, type OpenState } from "@/lib/shop-hours"
import { PulseDot } from "@/components/ui/pulse-dot"

// The closed card's red matches the notification badge (oklch(0.50 0.22 24)).

// Client-facing open/closed sign. Recomputes from the viewer's local clock on
// mount (and every minute) to avoid SSR/hydration drift. When closed it shows a
// friendly "opens back at X — book ahead" message so clients still reserve.
export function ShopHoursBanner({ hours, className }: { hours: ShopHour[] | null | undefined; className?: string }) {
  const [state, setState] = useState<OpenState | null>(null)

  useEffect(() => {
    const tick = () => setState(getOpenState(hours))
    tick()
    const t = setInterval(tick, 60_000)
    return () => clearInterval(t)
  }, [hours])

  if (!state) return null

  if (state.open) {
    return (
      <div className={`inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-3 py-1.5 ${className ?? ""}`}>
        <PulseDot color="#10b981" />
        <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
          Open now{state.closesAtLabel ? ` · ${state.closesAtLabel.replace("Open until", "until")}` : ""}
        </span>
      </div>
    )
  }

  // Light: barely-there glossy red tint (lighter at the top), 0.5px red
  // hairline stroke, red text. Dark: solid glossy red with white text — the
  // translucent tint disappears on a black page. Styled entirely with
  // `dark:` variants (not resolvedTheme) so the card always matches the
  // page's actual surface theme.
  return (
    <div
      className={`rounded-2xl border-[0.5px] px-4 py-3 text-center bg-gradient-to-b from-[oklch(0.50_0.22_24_/_0.10)] to-[oklch(0.50_0.22_24_/_0.05)] border-[oklch(0.50_0.22_24_/_0.35)] dark:from-[oklch(0.55_0.22_24)] dark:to-[oklch(0.47_0.22_24)] dark:border-white/20 ${className ?? ""}`}
    >
      <p className="text-sm font-semibold text-[oklch(0.50_0.22_24)] dark:text-white">Closed</p>
      <p className="mt-0.5 text-xs text-[oklch(0.50_0.22_24_/_0.85)] dark:text-white/90">
        {state.nextOpenLabel ? `${state.nextOpenLabel} · Book ahead` : "Book ahead to grab a slot"}
      </p>
    </div>
  )
}
